
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { loginApi } from "../config/api";


export const registerUserEmail = createAsyncThunk(
  "auth/registerUserEmail",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/user/register_user_email"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Email registration failed");
      }

      Cookies.set("email", email, { secure: true, sameSite: "Strict" });

      return { email, message: data.message, status: data.success };
    } catch (error) {
      console.error("REGISTER EMAIL ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const verifyUserOtp = createAsyncThunk(
  "auth/verifyUserOtp",
  async ({ email, userOtpInput, buyerId = null }, { rejectWithValue }) => {
    try {
      // Build URL with optional buyerId query param
      const url = buyerId
        ? `${loginApi("/user/register_user_email/verify")}/?buyerId=${buyerId}`
        : loginApi("/user/register_user_email/verify");

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, userOtpInput }),
      });

      const data = await response.json();
      const { user } = data;

      if (!response.ok) {
        throw new Error(data.message || "OTP verification failed");
      }

      return { email, userId: user?._id, success: data.success };
    } catch (error) {
      console.error("OTP VERIFICATION ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);


export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/user/resend/otp"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "OTP resend failed");
      }

      return { email, success: data.success, message: data.message };
    } catch (error) {
      console.error("OTP RESEND ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserPassword = createAsyncThunk(
  "auth/updateUserPassword",
  async ({ userId, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/user/register/password"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: userId, password }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Password update failed");
      }

      if (!data.token) {
        throw new Error("Token missing in response");
      }

      Cookies.set("authToken", data.token, {
        secure: true,
        sameSite: "Strict",
      });

      return {
        success: true,
        id: data.user._id,
        user: data.user,
        token: data.token,
      };
    } catch (error) {
      console.error("PASSWORD UPDATE ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);
export const updateUserNames = createAsyncThunk(
  "auth/updateUserNames",
  async ({ userId, firstName, lastName, authToken }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/user/register/names"),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: userId, firstName, lastName }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Names update failed");
      }

      return { success: true, message: data.message, id: data._id };
    } catch (error) {
      console.error("NAMES UPDATE ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const checkEmail = createAsyncThunk(
  "auth/checkEmail",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/auth/check-email"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Email check failed");
      }

      return {
        id: data.data.id,
        emailExists: data.data.emailExists,
        hasPassword: data.data.hasPassword,
        message: data.data.message,
      };
    } catch (error) {
      console.error("CHECK EMAIL ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/user/forgot-password"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Password reset request failed");
      }

      return { email, success: data.success, message: data.message };
    } catch (error) {
      console.error("RESET PASSWORD ERROR:", error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const resetPasswordConfirm = createAsyncThunk(
  "auth/resetPasswordConfirm",
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/user/reset-password"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, password: newPassword }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Password reset failed");
      }

      return { success: data.success, message: data.message };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        loginApi("/auth/login"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.data.message || "Login failed");
      }

      if (data.data.message === "User profile is missing") {
        return rejectWithValue({
          redirect: "/account-type",
          message: data.data.message,
        });
      }

      if (!data.data.token) {
        throw new Error("Token missing in response");
      }

      const decodedToken = jwtDecode(data.data.token);

      Cookies.set("authToken", data.data.token, {
        secure: true,
        sameSite: "Strict",
      });

      return {
        email,
        id: data.data.profile.createdBy,
        fullUser: data.data.personalDetails,
        userDetails: data.data,
        accountType: data.data.accountType,
        hasProfileDetails: data.data.hasProfileDetails,
        hasNameDetails: data.data.hasNameDetails,
        token: data.data.token,
        role: decodedToken.role,
        tenantId:
          decodedToken.tenantId ??
          data.data.profile?.tenant_id ??
          data.data.profile?.tenantId ??
          null,
        profile: data.data.profile,
        personalDetails: data.data.personalDetails
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const token = Cookies.get("authToken");

const isAuthenticated = !!token;

let initialTenantId = null;
if (token) {
  try {
    initialTenantId = jwtDecode(token).tenantId ?? null;
  } catch {
    initialTenantId = null;
  }
}

const initialState = {
  isAuthenticated,
  user: null,
  userDetails: null,
  id: null,
  profileId: null,
  role: null,
  tenantId: initialTenantId,
  profile: null,
  personalDetails: null,
  selectedSupplier: null,
  token: token || null,
  error: null,
  loading: false,
  passwordUpdated: false,
  email: null,
  otpVerified: false,
  message: null,
};


const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.profile = null;
      state.personalDetails = null;
      state.role = null;
      state.tenantId = null;
      state.token = null;
      state.id = null;
      state.profileId = null;

      Cookies.remove("authToken");
      Cookies.remove("email");
    },
    setAccountType: (state, action) => {
      state.role = action.payload;
    },
        setSelectedSupplier: (state, action) => {
      state.selectedSupplier = action.payload;
    },
    clearSelectedSupplier: (state) => {
      state.selectedSupplier = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(checkEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.emailExists = action.payload.emailExists;
        state.hasPassword = action.payload.hasPassword;
        state.message = action.payload.message;
      })
      .addCase(checkEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.id = action.payload.id;
        state.email = action.payload.email;
        state.profileId = action.payload.profile?._id;
        state.user = action.payload.fullUser;
        state.userDetails = action.payload;
        state.token = action.payload.token;
        state.tenantId = action.payload.tenantId ?? null;
        state.profile = action.payload.profile;
        state.personalDetails = action.payload.personalDetails;
        state.role = action.payload.accountType;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.profile = null;
        state.error = action.payload;
      })

      .addCase(registerUserEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUserEmail.fulfilled, (state, action) => {
        state.email = action.payload.email;
        state.message = action.payload.message;
        state.loading = false;
      })
      .addCase(registerUserEmail.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      .addCase(verifyUserOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyUserOtp.fulfilled, (state, action) => {
        state.otpVerified = true;
        state.user = action.payload.user;
        state.email = action.payload.email;
        state.loading = false;
      })
      .addCase(verifyUserOtp.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      .addCase(updateUserNames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserNames.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.id = action.payload.id;
      })
      .addCase(updateUserNames.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
        state.id = null;
      })
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserPassword.fulfilled, (state, action) => {
        state.passwordUpdated = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.id = action.payload.id;
        state.token = action.payload.token;
        state.loading = false;
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })

      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.email = action.payload.email;
        state.message = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, setAccountType,   setSelectedSupplier, clearSelectedSupplier, } = authSlice.actions;
export default authSlice.reducer;